import React, { useState, useEffect, useRef, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ExternalLink, BarChart, Palette, GraduationCap, HeartPulse, Clock, Megaphone } from 'lucide-react';
import { categories } from '../data/categories';
import { aiTools } from '../data/aiTools';
import { AITool } from '../types';
import { SEOHead } from '../components/SEOHead';
import { useSelection } from '../context/SelectionContext';

export const SimplifiedHome: React.FC = () => {
  const { resetSelection } = useSelection();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [filteredTools, setFilteredTools] = useState<AITool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const toolsRef = useRef<HTMLDivElement>(null);

  // Sort categories alphabetically
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (selectedCategory) {
      setIsLoading(true);
      
      // Simulate API call with timeout
      setTimeout(() => {
        try {
          const tools = aiTools.filter(tool => 
            tool.categories.includes(selectedCategory)
          );
          setFilteredTools(tools);
          
          // Scroll 75% of the way to the tools section
          if (toolsRef.current) {
            const toolsPosition = toolsRef.current.getBoundingClientRect().top + window.scrollY;
            const currentPosition = window.scrollY;
            const distance = toolsPosition - currentPosition;
            const scrollTarget = currentPosition + (distance * 0.75); // Scroll 75% of the way
            
            window.scrollTo({
              top: scrollTarget,
              behavior: 'smooth'
            });
          }
        } catch (err) {
          setError('Failed to load tools. Please try again.');
          console.error('Error filtering tools:', err);
        } finally {
          setIsLoading(false);
        }
      }, 600);
    } else {
      setFilteredTools([]);
    }
  }, [selectedCategory]);

  const handleCategoryClick = (categoryId: string) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryId);
    }
  };

  // Get difficulty level label
  const getDifficultyLabel = (levels: string[]) => {
    if (levels.includes('advanced')) return 'Hard';
    if (levels.includes('intermediate')) return 'Medium';
    return 'Easy';
  };

  // Get difficulty level color
  const getDifficultyColor = (levels: string[]) => {
    if (levels.includes('advanced')) return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    if (levels.includes('intermediate')) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
    return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
  };

  // Get pricing badge
  const getPricingBadge = (pricingType: string) => {
    switch (pricingType) {
      case 'free':
        return { text: 'Free', bgColor: 'bg-green-100 dark:bg-green-900/50', textColor: 'text-green-800 dark:text-green-300' };
      case 'paid':
        return { text: 'Paid', bgColor: 'bg-purple-100 dark:bg-purple-900/50', textColor: 'text-purple-800 dark:text-purple-300' };
      case 'freemium':
        return { text: 'Freemium', bgColor: 'bg-blue-100 dark:bg-blue-900/50', textColor: 'text-blue-800 dark:text-blue-300' };
      default:
        return { text: '', bgColor: '', textColor: '' };
    }
  };

  // Helper function to get the correct icon for a category
  const getCategoryIcon = (iconName: string) => {
    switch (iconName) {
      case 'megaphone':
        return <Megaphone size={20} />;
      case 'heart-pulse':
        return <HeartPulse size={20} />;
      case 'bar-chart':
        return <BarChart size={20} />;
      case 'graduation-cap':
        return <GraduationCap size={20} />;
      case 'clock':
        return <Clock size={20} />;
      case 'palette':
        return <Palette size={20} />;
      default:
        return <Sparkles size={20} />;
    }
  };

  // Get SEO data based on selected category
  const getSEOData = () => {
    if (!selectedCategory) {
      return {
        title: 'AI Tool Finder - Discover Personalized AI Tool Recommendations',
        description: 'Find the perfect AI tools for your needs with our personalized recommendation engine. Explore tools for marketing, healthcare, finance, education, and more.'
      };
    }

    const category = categories.find(c => c.id === selectedCategory);
    if (!category) return {};

    return {
      title: `${category.name} AI Tools - AI Tool Finder`,
      description: `Discover the best AI tools for ${category.name.toLowerCase()}. ${category.description} tailored to your specific needs and experience level.`,
      breadcrumbs: [
        { name: 'Home', url: '/' },
        { name: category.name, url: `/category/${category.id}` }
      ]
    };
  };

  const seoData = getSEOData();

  return (
    <>
      <SEOHead 
        title={seoData.title}
        description={seoData.description}
        breadcrumbs={seoData.breadcrumbs}
        schemaType={selectedCategory ? 'list' : 'website'}
        schemaData={filteredTools}
      />
      
      <div className="min-h-screen light-mode-bg text-gray-900 dark:text-gray-100">
        <main className="container mx-auto px-4 py-6 md:py-8">
          {/* Hero Section with consolidated header content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Find Your Perfect AI Tool</h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Discover the best AI tools for your specific needs with our simple, curated collection.
            </p>
          </motion.div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 p-4 rounded-lg mb-6">
              <p>{error}</p>
              <button 
                onClick={() => setError(null)} 
                className="mt-2 text-sm font-medium underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Categories Section */}
          <section className="mb-12">
            <h2 className="text-xl font-bold mb-6">Select a Category</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {sortedCategories.map((category) => (
                <motion.div
                  key={category.id}
                  whileHover={{ 
                    y: -5,
                    filter: "brightness(1.1)",
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`
                    cursor-pointer rounded-xl p-4 transition-all shadow-sm hover:shadow-md
                    ${selectedCategory === category.id 
                      ? 'bg-indigo-100 dark:bg-indigo-900/50 border-2 border-indigo-500' 
                      : 'light-mode-card border border-gray-200 dark:border-gray-700'}
                  `}
                  style={{ transition: "all 0.2s ease, filter 0.2s ease" }}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`
                      p-3 rounded-full mb-3 flex items-center justify-center
                      ${selectedCategory === category.id 
                        ? 'bg-indigo-500 text-white' 
                        : 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'}
                    `}>
                      {getCategoryIcon(category.icon)}
                    </div>
                    <h3 className="font-medium text-sm md:text-base text-gray-900 dark:text-gray-100">{category.name}</h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <AnimatePresence>
            {selectedCategory && (
              <motion.section
                ref={toolsRef}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-12"
              >
                <h2 className="text-xl font-bold mb-6">Recommended AI Tools</h2>
                
                {isLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="light-mode-card rounded-xl p-4 border border-gray-200 dark:border-gray-700 animate-pulse">
                        <div className="flex items-start mb-4">
                          <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full mr-3"></div>
                          <div className="flex-1">
                            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredTools.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTools.map((tool, index) => {
                      const difficultyLabel = getDifficultyLabel(tool.experienceLevels);
                      const difficultyColor = getDifficultyColor(tool.experienceLevels);
                      const pricingBadge = getPricingBadge(tool.pricingType);
                      
                      return (
                        <motion.div
                          key={tool.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ 
                            opacity: 1, 
                            y: 0,
                            transition: { delay: index * 0.1 }
                          }}
                          className="light-mode-card rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start mb-3">
                            <div className="w-12 h-12 rounded-full overflow-hidden mr-3 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                              <img 
                                src={tool.logo} 
                                alt={`${tool.name} logo`} 
                                className="w-full h-full object-cover"
                                loading="lazy"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1679083216051-aa510a1a2c0e?q=80&w=100&auto=format&fit=crop';
                                }}
                              />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900 dark:text-gray-100">{tool.name}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{tool.description}</p>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            {tool.useCases.slice(0, 3).map(useCase => (
                              <span 
                                key={useCase} 
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                              >
                                {useCase.replace('-', ' ')}
                              </span>
                            ))}
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <a
                              href={tool.tryNowLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium text-sm"
                            >
                              Visit Website <ExternalLink size={14} className="ml-1" />
                            </a>
                            
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs px-2 py-1 rounded-full ${difficultyColor}`}>
                                {difficultyLabel}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full ${pricingBadge.bgColor} ${pricingBadge.textColor}`}>
                                {pricingBadge.text}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-600 dark:text-gray-400">No tools found for this category.</p>
                  </div>
                )}
              </motion.section>
            )}
          </AnimatePresence>
        </main>

        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4 mt-auto">
          <div className="container mx-auto px-4 text-center text-gray-600 dark:text-gray-400 text-sm">
            <p>© 2025 AI Tool Finder. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};